import { FC } from "react";
import {
    Box,
    useBreakpointValue,
    Flex,
    Heading,
    Text,
    Divider,
} from "@smile2impress/components/relaunch";

import { CtaButton } from "components/CtaButton";

import { IllustratedListProps } from "./IllustratedList.types";
import { Column } from "./components";

export const IllustratedList: FC<IllustratedListProps> = ({
    content,
    headingAs,
}) => {
    const {
        title,
        subtitle,
        text,
        items,
        cta,
        withNumberHeading = false,
        withHeaderCta = false,
        withMobileCta = true,
        withDivider = false,
        mainCTA = false,
        withExpandableText = false,
    } = content;

    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });

    return (
        <Flex direction="column" px={[0, 0, 4]}>
            {withHeaderCta && cta ? (
                <Flex
                    justifyContent="space-between"
                    alignItems="flex-end"
                    mb={[3, 3, 4]}
                >
                    <Box maxW={["100%", "100%", "800px"]}>
                        <Heading textStyle="H2" color="text.base">
                            {title}
                        </Heading>
                        {!!text && (
                            <Text textStyle="body" mt={[1]}>
                                {text}
                            </Text>
                        )}
                    </Box>
                    {!isMobile && <CtaButton {...cta} />}
                </Flex>
            ) : (
                <>
                    <Box mb={[3, 3, 4]} maxW={["100%", "100%", "800px"]}>
                        <Heading
                            textStyle={["H2"]}
                            color="text.base"
                            mb={[1]}
                            as={headingAs}
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                        {subtitle && (
                            <Text
                                textStyle={"H3"}
                                textTransform="uppercase"
                                mt={1}
                                dangerouslySetInnerHTML={{ __html: subtitle }}
                            />
                        )}
                        {text && <Text textStyle={"body"}>{text}</Text>}
                    </Box>
                    <Box
                        borderTop="1px solid"
                        borderColor="base.gunmetal"
                        opacity="0.1"
                        mb={[0, 0, 4]}
                    />
                </>
            )}
            {withDivider && !isMobile && (
                <Divider
                    borderBottomWidth="1px"
                    borderColor="base.grey"
                    _dark={{
                        opacity: 0.1,
                        borderColor: "bg.baseInverse",
                    }}
                    mb={4}
                />
            )}
            <Box>
                {items.map((item, index) => (
                    <Flex
                        key={index}
                        my={[3, 3, 4]}
                        direction="column"
                        _after={{
                            content: '" "',
                            display: ["none", "none", "block"],
                            height: 0,
                            borderBottom: "1px solid",
                            borderColor: "base.gunmetal",
                            opacity: 0.1,
                            mt: [0, 0, 4],
                        }}
                        _dark={{
                            _after: {
                                borderColor: "bg.baseInverse",
                            },
                        }}
                        _first={{ mt: 0 }}
                        _last={{ mb: 0, _after: { display: "none" } }}
                    >
                        <>
                            <Column
                                {...item}
                                {...(withNumberHeading
                                    ? { numberHeading: index + 1 }
                                    : {})}
                                {...(withExpandableText && {
                                    withExpandableText: true,
                                })}
                            />
                        </>
                    </Flex>
                ))}
            </Box>
            {isMobile && cta && withMobileCta && !mainCTA && (
                <Box mt={3}>
                    <CtaButton {...cta} />
                </Box>
            )}
            {mainCTA && (
                <Box mt={3}>
                    <CtaButton {...mainCTA} />
                </Box>
            )}
        </Flex>
    );
};
